import { Badge, Button, Select } from '@cloudflare/kumo';
import { DataTable as Table } from './ui/DataTable';
import { User } from '../types';
import { ICONS } from './ui/icons';
import { ORG_ROLES, orgRoleLabel } from '../lib/orgRoles';
import { SkeletonTable } from './ui/Skeleton';

interface UserListProps {
  users: User[];
  loading: boolean;
  onEdit: (user: User) => void;
  onDelete: (id: string) => void;
  /** Papel de cada usuário na organização ativa (userId -> papel) */
  memberRoles?: Record<string, string>;
  /** Pode alterar papéis (members:manage) */
  canManageRoles?: boolean;
  /** Pode promover/rebaixar Donos (owners:manage) */
  canManageOwners?: boolean;
  onRoleChange?: (userId: string, role: string) => void;
}

export function UserList({ users, loading, onEdit, onDelete, memberRoles, canManageRoles, canManageOwners, onRoleChange }: UserListProps) {
  if (loading) {
    return (
      <SkeletonTable rows={6} cols={4} label="Carregando usuários" />
    );
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="!text-[var(--text-faint)]">Nenhum usuário encontrado.</p>
      </div>
    );
  }

  const columnDefinitions = [
    {
      id: 'nome',
      header: 'Nome',
      cell: (user: User) => <p className="font-medium !text-[var(--text)]">{user.nome}</p>,
    },
    {
      id: 'email',
      header: 'E-mail',
      cell: (user: User) => <p className="!text-[var(--text-muted)]">{user.email}</p>,
    },
    {
      id: 'papel',
      header: 'Papel na organização',
      cell: (user: User) => {
        const role = memberRoles?.[user.id];
        if (!role) {
          return <Badge variant={user.role === 'ADMIN' ? 'neutral' : 'blue'}>{user.role === 'ADMIN' ? 'Administrador' : 'Usuário'}</Badge>;
        }
        const editable = canManageRoles && onRoleChange && (canManageOwners || role !== 'OWNER');
        if (!editable) return <Badge variant="neutral">{orgRoleLabel(role)}</Badge>;
        const options = ORG_ROLES
          .filter((r) => canManageOwners || r.value !== 'OWNER')
          .map((r) => ({ value: r.value, label: r.label, description: r.description }));
        return (
          <div style={{ width: 230 }}>
            <Select value={(options.find((o) => o.value === role) ?? null)?.value ?? null} items={options} onValueChange={(value: string | null) => {
                if (value && value !== role) onRoleChange!(user.id, value);
              }} aria-label={`Papel de ${user.nome}`} />
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      cell: (user: User) => (
        <Badge variant={user.ativo ? 'green' : 'red'}>
          {user.ativo ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      id: 'ultimoLogin',
      header: 'Último Login',
      cell: (user: User) => (
        <p className="!text-[var(--text-muted)] text-xs">
          {user.ultimoLogin
            ? new Date(user.ultimoLogin).toLocaleString('pt-BR')
            : 'Nunca'}
        </p>
      ),
    },
    {
      id: 'acoes',
      header: 'Ações',
      cell: (user: User) => (
        canManageRoles !== false ? (
          <div className="flex gap-1 items-center">
            <Button variant="ghost" shape="square" icon={ICONS.Edit} aria-label={`Editar ${user.nome}`} title={'Editar'} onClick={() => onEdit(user)} />
            <Button variant="ghost" shape="square" icon={ICONS.Trash} aria-label={`Remover ${user.nome} da organização`} title={'Remover da organização'} onClick={() => onDelete(user.id)} />
          </div>
        ) : null
      ),
    },
  ];

  return (
    <div className="overflow-x-auto">
      <Table
        items={users}
        columnDefinitions={columnDefinitions}
        trackBy={(user: User) => user.id}
        wrapLines
        ariaLabels={{
          tableLabel: 'Usuários',
          selectionGroupLabel: 'Usuários',
        }}
      />
    </div>
  );
}
